import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'store';

import { selectAppointments } from '~appointments/selectors/appointments';

const UnallocatedState = (state: RootState) => state.weeklyPlanner.unallocated;
const UnsavedState = (state: RootState) => state.weeklyPlanner.unsaved;

const selectRows = (state: RootState) => UnallocatedState(state).rows;

export const selectLoading = (state: RootState) => UnallocatedState(state).loading;
export const selectHasLoaded = (state: RootState) => UnallocatedState(state).hasLoaded;
export const selectParams = (state: RootState) => UnallocatedState(state).params;
export const selectCount = (state: RootState) => UnallocatedState(state).count;
export const selectOptions = (state: RootState) => UnallocatedState(state).options;

const selectUnsavedChanges = (state: RootState) => UnsavedState(state).appointments;

export const selectAll = createSelector([selectRows], (rows) =>
  rows.map((row) => ({ ...row, key: row.id ?? row.parent_repeat_id })),
);

// Define a type for AppointmentRow based on the shape of rows
type AppointmentRow = ReturnType<typeof selectRows>[number];

// Define a type for UnsavedChange if not already defined
type UnsavedChange = {
  original: Partial<AppointmentRow>;
  change: Partial<AppointmentRow>;
};

// Helper to merge appointment data and unsaved changes
function mergeAppointmentData(
  base: AppointmentRow | undefined,
  original: Partial<AppointmentRow>,
  change: Partial<AppointmentRow>,
  key: string | number,
): AppointmentRow {
  return {
    ...(base ?? {}),
    ...original,
    ...change,
    key,
  } as AppointmentRow;
}

export const selectUnallocated = createSelector(
  [selectAll, selectUnsavedChanges, selectAppointments, selectParams],
  (rows, unsavedChanges, allAppointments, params) => {
    const unsavedMap = new Map<string, UnsavedChange>(Object.entries(unsavedChanges));
    const unallocated: AppointmentRow[] = [];

    // Build a map for fast lookup by id and parent_repeat_id
    const appointmentMap = new Map<string, any>();
    for (const appointment of allAppointments) {
      if (appointment.id !== undefined && appointment.id !== null) {
        appointmentMap.set(String(appointment.id), appointment);
      }
      if (appointment.parent_repeat_id !== undefined && appointment.parent_repeat_id !== null) {
        appointmentMap.set(String(appointment.parent_repeat_id), appointment);
      }
    }

    // Add unallocated appointments from original rows, considering unsaved changes
    for (const row of rows) {
      const record = unsavedMap.get(String(row.key));
      if (record?.change.user_id === null) {
        const merged = mergeAppointmentData(row, record.original, record.change, row.key ?? '');
        if (matchesParams(merged, params)) unallocated.push(merged);
      } else if (!record && row.user_id === null) {
        if (matchesParams(row, params)) unallocated.push(row);
      }
    }    

    // sort the unallocated the same way the list is sorted in the reducer  (by date, then by time)
    unallocated.sort((a, b) => {
      if (a.date !== b.date) {
        return a.date < b.date ? -1 : 1;
      }
      if (a.start_time !== b.start_time) {
        return a.start_time < b.start_time ? -1 : 1;
      }
      return 0;
    });

    // Add new unallocated appointments from unsavedChanges not already included to the top of the list
    const unsavedAppointments: AppointmentRow[] = [];
    for (const [key, record] of unsavedMap.entries()) {
      const alreadyIncluded = unallocated.some((row) => String(row.key) === key);
      if (record.change.user_id === null && !alreadyIncluded) {
        const realAppointment = appointmentMap.get(key);
        const merged = mergeAppointmentData(realAppointment, record.original, record.change, key);
        if (matchesParams(merged, params)) unsavedAppointments.push(merged);
      }
    }
    // Collect all unsaved appointments then unshift them to the top of the list
    // Only perform unshift once instead of in the for loop as unshift calls are O(n.k)
    unallocated.unshift(...unsavedAppointments.reverse());

    return unallocated;
  },
);
const matchesParams = (appointment: AppointmentRow, params: { [key: string]: any }) => {
  if (params.client_id && appointment.client?.id !== params.client_id) return false;
  if (params.user_id && appointment.user_id !== params.user_id) return false;
  if (params.suburb && appointment.client?.suburb?.toLowerCase() !== params.suburb.toLowerCase()) return false;
  if (params.date && appointment.date !== params.date) return false;
  return true;
};

export const selectAdjustedUnallocatedCount = createSelector(
  [selectAll, selectUnsavedChanges, selectAppointments, selectParams, selectCount],
  (rows, unsavedChanges, allAppointments, params, count) => {
    // Build a map for fast lookup by id and parent_repeat_idq
    const appointmentMap = new Map<string, any>();
    for (const appointment of allAppointments) {
      if (appointment.id !== undefined && appointment.id !== null) {
        appointmentMap.set(String(appointment.id), appointment);
      }
      if (appointment.parent_repeat_id !== undefined && appointment.parent_repeat_id !== null) {
        appointmentMap.set(String(appointment.parent_repeat_id), appointment);
      }
    }

    const newlyAllocatedKeys = new Set<string>();
    const newlyUnallocatedKeys = new Set<string>();

    for (const [key, record] of Object.entries(unsavedChanges)) {
      // If the original was unallocated and now has a user_id, it's newly allocated
      if (
        (record.original.user_id === null || record.original.user_id === undefined) &&
        record.change.user_id !== null &&
        record.change.user_id !== undefined
      ) {
        // Only count if it matches the filter after allocation
        const realAppointment = appointmentMap.get(key);
        const merged = mergeAppointmentData(realAppointment, record.original, record.change, key);
        if (matchesParams(merged, params)) {
          newlyAllocatedKeys.add(key);
        }
      }

      // If the original was allocated and now has user_id null, it's newly unallocated
      if (record.original.user_id !== null && record.original.user_id !== undefined && record.change.user_id === null) {
        // Only count if it matches the filter after unallocation
        const realAppointment = appointmentMap.get(key);
        const merged = mergeAppointmentData(realAppointment, record.original, record.change, key);
        // Only add if not already present in the base unallocated rows
        const alreadyInBase = rows.some(
          (row) => String(row.key) === key && row.user_id === null && matchesParams(row, params),
        );
        if (matchesParams(merged, params) && !alreadyInBase) {
          newlyUnallocatedKeys.add(key);
        }
      }
    }

    // The total count from the reducer (unallocated reducer) is the base
    let adjustedCount = count ?? 0;

    // Subtract newly allocated (they are no longer unallocated)
    adjustedCount -= newlyAllocatedKeys.size;

    // Add newly unallocated (they are now unallocated)
    adjustedCount += newlyUnallocatedKeys.size;

    return adjustedCount;
  },
);
